import { z } from 'zod';

import { PageQuerySchema, SearchQuerySchema } from '../shared/pagination';
import { McpToolRefSchema } from './mcp';
import { AgentModelTierSchema } from './model-tiers';

// Local (code-defined) tools — a closed enum, since adding one is always a code change.
// The six kb-* tools are three retrieval ops (index / read / search) at two fixed
// visibility scopes: the customer set (kb-*) and the internal set (kb-internal-*). The
// scope is hard-coded inside each handler and can never be widened by a tool argument.
// record-verdict pins its outcome kind and owning session the same way, and recent-verdicts
// pins the same kind on the read side. The three code-* / git-history tools are pinned to one
// read-only clone of the SBC application repo — the path jail and the git subcommand set live
// in the handlers, not in their arguments. stripe-read is pinned to four GET endpoints on a
// restricted read-only key; no argument reaches a path the enum does not name. record-draft
// pins its outcome kind like record-verdict. record-reply is the channel agents' concluding
// act — it pins the same way, and the gateway sends only what it recorded, never the run's
// last-message prose.
// record-job-result is the same act for a run SBC asked for: pinned kind and turn, and the only
// thing whose shape is the agent prompt's business rather than this package's.
// The read en-kr-glossary-* tool is pinned to the approved glossary article; the propose tool
// stores a pending proposal that only a signed-in admin can approve into it — an agent never
// writes the glossary itself.
export const AGENT_TOOLS = [
  'handoff',
  'find-products',
  'get-product',
  'kb-index',
  'kb-read',
  'kb-search',
  'kb-internal-index',
  'kb-internal-read',
  'kb-internal-search',
  'cs-macros',
  'intercom-attachment-read',
  'intercom-member-history',
  'member-channel-history',
  'slack-cs-help',
  'db-query',
  'code-search',
  'code-read',
  'git-history',
  'record-verdict',
  'recent-verdicts',
  'record-reply-draft',
  'record-draft',
  'record-draft-claims',
  'record-reply',
  'record-job-result',
  'stripe-read',
  'sbc-customer-profile',
  'sbc-customer-subscription',
  'sbc-customer-orders',
  'sbc-customer-schedule',
  'sbc-customer-charges',
  'sbc-customer-account-history',
  'sbc-customer-lookup',
  'sbc-gift-card',
  'address-check',
  'carrier-tracking',
  'site-incidents',
  'sbc-product-stock',
  'sbc-beauty-profile',
  'sbc-product-info',
  'sbc-curation-status',
  'klaviyo-member-emails',
  'product-registration-read-product',
  'product-registration-hs-code-search',
  'product-registration-check-document',
  'product-registration-generate-thumbnail',
  'cs-resubscribe-code',
  'en-kr-glossary-read',
  'en-kr-glossary-propose-term',
  'sbc-promo-code',
  'sbc-referrals',
  'record-proposal',
] as const;
export const LocalAgentToolSchema = z.enum(AGENT_TOOLS);
export type LocalAgentTool = z.infer<typeof LocalAgentToolSchema>;

// Presentation metadata for the local tools: the operator-facing label and the group it
// is picked under in the admin. Keyed by the enum, so adding a tool to AGENT_TOOLS without
// naming its label and group fails the typecheck gate rather than shipping an unlabelled
// checkbox. `handoff` sits under 'agents' alongside the data-defined `agent.*` refs, which
// are not in this catalog because they come from the available-tools endpoint at runtime.
// The customer and internal knowledge-base sets stay two groups rather than one: the
// visibility scope is the safety-relevant difference between them, so it has to be legible
// without expanding anything.
export const LOCAL_TOOL_GROUPS = [
  'agents',
  'kb-customer',
  'kb-internal',
  'glossary',
  'product-catalog',
  'sbc-data',
  'sbc-source',
  'product-registration',
  'outcomes',
] as const;
export type LocalToolGroup = (typeof LOCAL_TOOL_GROUPS)[number];

export const LOCAL_TOOL_GROUP_LABELS: Record<LocalToolGroup, string> = {
  agents: 'Agents',
  'kb-customer': 'Knowledge base — customer',
  'kb-internal': 'Knowledge base — internal',
  glossary: 'EN↔KR glossary',
  'product-catalog': 'Product catalog',
  'sbc-data': 'SBC data',
  'sbc-source': 'SBC source',
  'product-registration': 'Product registration',
  outcomes: 'Outcomes',
};

export const LOCAL_TOOL_CATALOG: Record<
  LocalAgentTool,
  { readonly label: string; readonly group: LocalToolGroup }
> = {
  handoff: { label: 'Handoff to another agent', group: 'agents' },
  'find-products': { label: 'Find products (catalog API preview)', group: 'product-catalog' },
  'get-product': { label: 'Get product detail (catalog API preview)', group: 'product-catalog' },
  'kb-index': { label: 'Index', group: 'kb-customer' },
  'kb-read': { label: 'Read article', group: 'kb-customer' },
  'kb-search': { label: 'Search', group: 'kb-customer' },
  'kb-internal-index': { label: 'Index', group: 'kb-internal' },
  'kb-internal-read': { label: 'Read article', group: 'kb-internal' },
  'kb-internal-search': { label: 'Search', group: 'kb-internal' },
  'cs-macros': { label: 'CS macros (live from Intercom)', group: 'kb-internal' },
  'intercom-attachment-read': {
    label: 'Open an image the member attached (Intercom)',
    group: 'sbc-data',
  },
  'intercom-member-history': {
    label: "This member's earlier Intercom conversations",
    group: 'sbc-data',
  },
  'member-channel-history': {
    label: "This member's Instagram and Facebook DMs (social inbox)",
    group: 'sbc-data',
  },
  'slack-cs-help': {
    label: 'What the team and ACI said about this member in Slack #cs-help and #ext-aci-cs',
    group: 'sbc-data',
  },
  'db-query': { label: 'SBC database: read-only SQL query', group: 'sbc-data' },
  'code-search': { label: 'Search', group: 'sbc-source' },
  'code-read': { label: 'Read a file', group: 'sbc-source' },
  'git-history': { label: 'Log, blame, show', group: 'sbc-source' },
  'record-verdict': { label: 'Record a root-cause verdict', group: 'outcomes' },
  'recent-verdicts': {
    label: 'Recently confirmed failure classes (dedupe)',
    group: 'outcomes',
  },
  'record-reply-draft': { label: 'Record a social-inbox reply draft', group: 'outcomes' },
  'record-draft': { label: 'Record a CS reply draft', group: 'outcomes' },
  'record-draft-claims': {
    label: 'Record what a CS reply draft claims (verifier)',
    group: 'outcomes',
  },
  'record-reply': { label: 'Record the reply to send on this channel', group: 'outcomes' },
  'record-job-result': {
    label: 'Record the result of the job SBC asked for',
    group: 'outcomes',
  },
  'stripe-read': {
    label: 'Stripe: read a customer, charge, invoice, or subscription',
    group: 'sbc-data',
  },
  'sbc-customer-profile': { label: "This customer's account profile", group: 'sbc-data' },
  'sbc-customer-subscription': {
    label: "This customer's subscription and starter trial",
    group: 'sbc-data',
  },
  'sbc-customer-orders': { label: "This customer's recent orders", group: 'sbc-data' },
  'sbc-customer-schedule': { label: "This customer's box schedule", group: 'sbc-data' },
  'sbc-customer-charges': { label: "This customer's Stripe charges", group: 'sbc-data' },
  'sbc-customer-account-history': {
    label: "This customer's credit history, admin notes and admin actions",
    group: 'sbc-data',
  },
  'sbc-customer-lookup': {
    label: 'Look up an account by an email, order or tracking number the member gave',
    group: 'sbc-data',
  },
  'sbc-gift-card': { label: "Gift cards (by code, or this customer's)", group: 'sbc-data' },
  'address-check': {
    label: "Check this customer's shipping address (EasyPost)",
    group: 'sbc-data',
  },
  'carrier-tracking': {
    label: 'Track any parcel by its tracking number (USPS, UniUni)',
    group: 'sbc-data',
  },
  'site-incidents': { label: 'Current member-site errors (Sentry)', group: 'sbc-data' },
  'cs-resubscribe-code': {
    label: "Create this customer's 30% resubscribe code (Stripe write)",
    group: 'sbc-data',
  },
  'sbc-product-stock': { label: 'Product stock and in-stock swaps', group: 'sbc-data' },
  'sbc-beauty-profile': {
    label: "This customer's beauty profile and allergies",
    group: 'sbc-data',
  },
  'sbc-product-info': {
    label: 'Product facts: brand, size, premium, ingredients, stock',
    group: 'sbc-data',
  },
  'sbc-curation-status': {
    label: "This customer's box curation: picks, allowance, auto-curation",
    group: 'sbc-data',
  },
  'klaviyo-member-emails': {
    label: 'Marketing emails Klaviyo sent this customer (last 45 days)',
    group: 'sbc-data',
  },
  'product-registration-read-product': { label: 'Read one product', group: 'product-registration' },
  'product-registration-hs-code-search': {
    label: 'Search HS codes',
    group: 'product-registration',
  },
  'product-registration-check-document': {
    label: 'Check the MSDS',
    group: 'product-registration',
  },
  'product-registration-generate-thumbnail': {
    label: 'Generate the thumbnail',
    group: 'product-registration',
  },
  'en-kr-glossary-read': { label: 'Read the approved glossary', group: 'glossary' },
  'en-kr-glossary-propose-term': {
    label: 'Propose a term (an admin approves it into the glossary)',
    group: 'glossary',
  },
  'sbc-promo-code': {
    label: 'Check a promo, coupon or referral code from the conversation',
    group: 'sbc-data',
  },
  'sbc-referrals': { label: "This customer's referrals and referral credits", group: 'sbc-data' },
  'record-proposal': {
    label: 'Record a draft-review proposal (KB change, prompt rule, scores)',
    group: 'outcomes',
  },
};

// The loop matches a tool-call's `toolName` against this constant by string
// equality to detect a handoff call and terminate/reroute the run.
export const HANDOFF_TOOL: AgentTool = 'handoff';

// A data-defined ref to a peer agent, exposed to a caller as a tool. Must be matched
// BEFORE McpToolRefSchema in the union: "agent.<slug>" also satisfies the MCP ref regex.
export const AgentRefSchema = z
  .string()
  .regex(/^agent\.[a-z0-9]+(?:-[a-z0-9]+)*$/)
  .max(63)
  .brand<'AgentRef'>();
export type AgentRef = z.infer<typeof AgentRefSchema>;
export const isAgentRef = (ref: string): ref is AgentRef => AgentRefSchema.safeParse(ref).success;
export const agentRefSlug = (ref: AgentRef): string => ref.slice('agent.'.length);

// A tool is a local tool, a peer-agent ref, or a data-defined MCP tool ref. Whether a
// given MCP ref is actually allowlisted is a runtime service check, not part of this schema.
export const AgentToolSchema = z.union([LocalAgentToolSchema, AgentRefSchema, McpToolRefSchema]);
export type AgentTool = z.infer<typeof AgentToolSchema>;

// --- Available tools (catalog + agent picker) ---
export const AvailableToolSchema = z.object({
  ref: AgentToolSchema,
  label: z.string(),
  connectionId: z.string().optional(),
  toolClass: z.enum(['read', 'write']).optional(),
});
export type AvailableTool = z.infer<typeof AvailableToolSchema>;
export const AvailableToolsSchema = z.object({
  local: z.array(LocalAgentToolSchema),
  agents: z.array(AvailableToolSchema),
  mcp: z.array(AvailableToolSchema),
});
export type AvailableTools = z.infer<typeof AvailableToolsSchema>;

// Per-run Gemini request budget. Lives on the spec so a chatty CS agent and a
// code-reading investigator can each carry their own ceiling; the default is the
// number the loop used as a global constant, so stored versions keep behaving
// exactly as before.
export const DEFAULT_MAX_REQUESTS_PER_RUN = 10;

// Capped at 57, not the seemingly-natural 63: the slug is always embedded as
// `agent.<slug>` in AgentRefSchema, which caps at 63 to fit Gemini's function-name
// limit. `'agent.'.length` (6) eats into that budget, so 57 is the max slug length
// that still leaves a valid ref.
export const SlugSchema = z
  .string()
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)
  .max(57);

// The full editable spec = the content of one version, slug included. Create and
// update share this exact shape — every write appends a new version.
export const AgentSpecSchema = z.object({
  slug: SlugSchema,
  name: z.string().min(1).max(120),
  description: z.string().max(2000),
  modelTier: AgentModelTierSchema,
  systemPrompt: z.string().min(1),
  // Voice/tone instructions, versioned alongside the behavioral prompt. Composed
  // into the session system prompt at seed time; empty means "no voice section".
  voicePrompt: z.string().default(''),
  maxRequestsPerRun: z.number().int().min(1).default(DEFAULT_MAX_REQUESTS_PER_RUN),
  enabledTools: z.array(AgentToolSchema).default([]),
});
export type AgentSpec = z.infer<typeof AgentSpecSchema>;

// Immutable config snapshot — the spec plus server-derived provenance.
export const AgentVersionSchema = AgentSpecSchema.extend({
  id: z.uuid(),
  agentId: z.uuid(),
  versionNumber: z.number().int().positive(), // monotonic per agent, starts at 1

  // who authored this version — server-derived from the authenticated admin,
  // never part of the write contract. Resolved to the admin's email for display.
  updatedByEmail: z.email(),
  createdAt: z.iso.datetime(),
});
export type AgentVersion = z.infer<typeof AgentVersionSchema>;

// Agent — opaque identity + lifecycle pointers. The routing key is the active
// version's slug, never this id.
export const AgentSchema = z.object({
  id: z.uuid(),
  activeVersionId: z.uuid(),
  latestVersionNumber: z.number().int().positive(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type Agent = z.infer<typeof AgentSchema>;

// list row: identity + the active version resolved.
export const AgentSummarySchema = AgentSchema.extend({
  activeVersion: AgentVersionSchema,
});
export type AgentSummary = z.infer<typeof AgentSummarySchema>;

// detail page: identity + ALL versions (newest first) — backs the version
// dropdown. The active one is the version whose id === activeVersionId.
export const AgentDetailSchema = AgentSchema.extend({
  versions: z.array(AgentVersionSchema),
});
export type AgentDetail = z.infer<typeof AgentDetailSchema>;

// create the agent + its v1.
export const CreateAgentSchema = AgentSpecSchema;
export type CreateAgent = z.infer<typeof CreateAgentSchema>;

// an "edit" — server appends a new version and (by default) advances active.
export const UpdateAgentSchema = AgentSpecSchema;
export type UpdateAgent = z.infer<typeof UpdateAgentSchema>;

// explicit promote/rollback — repoint active to an existing version.
export const SetActiveVersionSchema = z.object({
  versionId: z.uuid(),
});
export type SetActiveVersion = z.infer<typeof SetActiveVersionSchema>;

// agents list = paging + search.
export const ListAgentsQuerySchema = PageQuerySchema.merge(SearchQuerySchema);
export type ListAgentsQuery = z.infer<typeof ListAgentsQuerySchema>;

// page envelope for the agents list.
export const AgentPageSchema = z.object({
  items: z.array(AgentSummarySchema),
  page: z.number().int(),
  pageSize: z.number().int(),
  total: z.number().int(),
});
export type AgentPage = z.infer<typeof AgentPageSchema>;
